import { Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import _maxBy from "lodash/maxBy"
import React, { useMemo } from "react"
import styled from "styled-components"

const Wrapper = styled.div`
  > header {
    padding-left: 100px;
  }
`

const BarsWrap = styled.div`
  padding: 1rem 0;
`

const BarRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 3px 0.5rem 0 0;

  > label {
    width: 100px;
    text-align: right;
    padding: 0 0.5rem 0 0;
  }

  > div {
    position: relative;
    width: 100%;
  }
`

const Bar = styled.div<{
  color: string
  max: number
  showValue: boolean
  value: number
}>`
  background: ${p => p.color};
  width: ${p => Math.min(100, (100 * p.value) / p.max)}%;
  height: 100%;
  position: absolute;
  ${p =>
    p.showValue
      ? `&::after {
      content: '${p.value}';
      color: white;
      position: absolute;
      right: -30px;
      width: 30px;
      text-align: center;
    }`
      : ""}
`

const BarShadow = styled.div<{ isHighlighted: boolean }>`
  background: ${p =>
    p.isHighlighted
      ? "rgba(255, 255, 255, 0.15)"
      : "rgba(255, 255, 255, 0.05)"};
  border: 1px
    ${p =>
      p.isHighlighted
        ? "solid rgba(255, 255, 255, 0.5)"
        : "dotted rgba(255, 255, 255, 0.15)"};
  width: 100%;
  height: 100%;
  position: absolute;
`

type TBarsDatum = {
  label: string
  value: number
}

const colorKeys: string[] = ["greens", "blues", "pinks", "light"]

interface IBarsProps {
  /**
   * An index used to pick a color scale for the bars, from the theme
   * @default 0
   */
  colorType: 0 | 1 | 2 | 3
  /**
   * The bars data
   * @default []
   */
  data: TBarsDatum[]
  /**
   * The max bar value. If not supplied, the max value from the data will be used.
   */
  max: number
  /**
   * A single bar index to highlight, to draw attention to
   */
  highlightIndex: number
  /**
   * Whether to show values to the right of the bars
   * @default true
   */
  showValues: boolean
  /**
   * Additional styles to be added to the wrapper for layout purposes
   * @default {}
   */
  styles: React.CSSProperties
  /**
   * A subtitle to be displayed above the bars
   * @default ""
   */
  subtitle: string
  /**
   * A title to be displayed above the bars
   * @default ""
   */
  title: string
}

export const Bars = ({
  colorType = 0,
  data = [],
  highlightIndex = -1,
  max,
  showValues = true,
  styles = {},
  subtitle = "",
  title = ""
}: IBarsProps) => {
  const theme = useTheme()
  const colorScales = theme.colorScales
  const colorScaleKey: string = colorKeys[colorType]
  const colorScale =
    colorScales && colorScaleKey in colorScales
      ? colorScales[colorScaleKey]
      : null

  const filteredData = useMemo(
    () =>
      data.filter(
        d =>
          Object.keys(d).includes("label") &&
          Object.keys(d).includes("value") &&
          typeof d.label === "string" &&
          typeof d.value === "number"
      ),
    []
  )

  if (!filteredData || !filteredData.length) {
    return <p>No Data</p>
  }

  const maxValElm = _maxBy(filteredData, "value")
  if (!maxValElm || typeof maxValElm === "undefined" || !maxValElm.value) {
    return <p>Invalid Data</p>
  }

  const maxVal = max && !Number.isNaN(max) ? max : maxValElm.value
  return (
    <Wrapper style={{ ...styles }}>
      <header>
        {title ? <Typography variant="h4">{title}</Typography> : ""}
        {subtitle ? <Typography>{subtitle}</Typography> : ""}
      </header>
      <BarsWrap>
        {filteredData.map((d: TBarsDatum, i: number) => (
          <BarRow key={d.label}>
            <label>{d.label}</label>
            <div>
              <BarShadow isHighlighted={i === highlightIndex} />
              <Bar
                value={d.value}
                max={maxVal}
                color={
                  colorScale
                    ? colorScale(1 - i / (filteredData.length - 1)).css()
                    : "black"
                }
                showValue={showValues}
                title={`${d.value}`}
              />
            </div>
          </BarRow>
        ))}
      </BarsWrap>
    </Wrapper>
  )
}

export default Bars
